import React, { useCallback, useEffect, useRef, useState } from 'react';

import Row from 'components/Common/Row';
import useIsScrolling from 'components/ShadowedList/useIsScrolling';
import Button from 'kingpin/atoms/Button';

const SCROLL_STEP_RATIO = 0.7;

const Fade = ({ side }: { side: 'left' | 'right' }) => (
  <div
    style={{
      position: 'absolute',
      top: 0,
      bottom: 0,
      [side]: 0,
      width: 40,
      pointerEvents: 'none',
      background: `linear-gradient(to ${side === 'left' ? 'right' : 'left'}, var(--color-bg-primary), transparent)`,
    }}
  />
);

// Fades the ends of an overflowing quick filter row and shows an arrow button
// on each side that has more filters to page to, rather than a scroll bar.
const QuickFiltersScroller = ({ children }: { children: React.ReactNode }) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isOverflowing = useIsScrolling({
    scrollerRef,
    contentRef,
    isHorizontalScroll: true,
  });
  const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false);
  const [canScrollRight, setCanScrollRight] = useState<boolean>(false);

  const updateArrows = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return;
    }

    setCanScrollLeft(scroller.scrollLeft > 0);
    setCanScrollRight(
      Math.ceil(scroller.scrollLeft + scroller.clientWidth) <
        scroller.scrollWidth,
    );
  }, []);

  // Showing or hiding an arrow changes the visible width, so re-measure
  // whenever either does.
  useEffect(() => {
    updateArrows();
  }, [isOverflowing, canScrollLeft, canScrollRight, updateArrows]);

  const move = useCallback(({ right }: { right: boolean }) => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return;
    }

    scroller.scrollBy({
      top: 0,
      left: scroller.clientWidth * SCROLL_STEP_RATIO * (right ? 1 : -1),
      behavior: 'smooth',
    });
  }, []);

  return (
    <Row centerAlign style={{ width: '100%', height: '100%', gap: 8 }}>
      {canScrollLeft && (
        <div style={{ flexShrink: 0 }}>
          <Button
            size="Small"
            type="Tertiary"
            icon="chevron-left"
            onClick={() => move({ right: false })}
            testId="quick-filters-scroll-left"
          />
        </div>
      )}
      <div
        style={{ position: 'relative', flex: 1, minWidth: 0, height: '100%' }}
      >
        <Row
          ref={scrollerRef}
          onScroll={updateArrows}
          centerAlign
          style={{ height: '100%', overflow: 'hidden' }}
        >
          <Row ref={contentRef} centerAlign style={{ flexShrink: 0 }}>
            {children}
          </Row>
        </Row>
        {canScrollLeft && <Fade side="left" />}
        {canScrollRight && <Fade side="right" />}
      </div>
      {canScrollRight && (
        <div style={{ flexShrink: 0 }}>
          <Button
            size="Small"
            type="Tertiary"
            icon="chevron-right"
            onClick={() => move({ right: true })}
            testId="quick-filters-scroll-right"
          />
        </div>
      )}
    </Row>
  );
};

export default QuickFiltersScroller;
